import React, { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, TrendingUp, Building2, DollarSign, 
  Gift, RefreshCw, Target, UserCheck, Award
} from 'lucide-react';
import { toast } from 'sonner';

const ReferralDashboard = ({ user }) => {
  const [period, setPeriod] = useState('MTD');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, [period]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.getReferralDashboard(period);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load referral dashboard:', error);
      toast.error('Failed to load referral dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatAV = (avLacs) => {
    if (!avLacs || avLacs === 0) return '₹0';
    if (avLacs >= 100) {
      return `₹${(avLacs / 100).toFixed(2)} Cr`;
    }
    return `₹${avLacs.toFixed(2)} L`;
  };

  const MetricCard = ({ icon: Icon, iconColor, title, value, subValue, footer }) => (
    <Card className="p-4 bg-white shadow-sm rounded-2xl">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-xl ${iconColor}`}>
          <Icon size={20} className="text-white" />
        </div>
      </div>
      <p className="text-2xl font-black text-[#111827]">{value}</p>
      {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
      <p className="text-xs text-slate-400 mt-2">{footer}</p>
    </Card>
  );

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <RefreshCw size={24} className="animate-spin text-[#5B3DF5]" />
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const penetration = metrics.referral_penetration || {};
  const adoption = metrics.rep_adoption || {};
  const bookings = metrics.referral_bookings || {};
  const statusBreakdown = data?.status_breakdown || {};

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 md:px-8 md:py-4 shadow-sm sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg md:text-xl font-bold text-[#111827]">Referral Dashboard</h1>
            <p className="text-xs text-slate-400">Track referral performance & payouts</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="period-select" className="w-[100px] h-9 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MTD">MTD</SelectItem>
                <SelectItem value="QTD">QTD</SelectItem>
                <SelectItem value="FYTD">FYTD</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              variant="outline"
              size="sm"
              onClick={fetchDashboard}
              className="h-9 rounded-xl"
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 md:px-8 md:py-6">
        <div className="max-w-[1400px] mx-auto space-y-6">
          
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              icon={Target}
              iconColor="bg-purple-500"
              title="Referral Penetration"
              value={`${penetration.rate || 0}%`}
              subValue={`${penetration.referred_customers || 0} / ${penetration.total_customers || 0} customers`}
              footer="Referred vs Total Customers Met"
            />
            
            <MetricCard
              icon={UserCheck}
              iconColor="bg-blue-500"
              title="Rep Adoption"
              value={`${adoption.rate || 0}%`}
              subValue={`${adoption.reps_with_referrals || 0} / ${adoption.total_reps || 0} reps`}
              footer="Reps with Referral Activity"
            />
            
            <MetricCard
              icon={Building2}
              iconColor="bg-emerald-500"
              title="Referral Bookings"
              value={bookings.total_bookings || 0}
              subValue={formatAV(bookings.total_av_lacs)}
              footer="Total Agreement Value"
            />
            
            <MetricCard
              icon={Gift}
              iconColor="bg-amber-500"
              title="Benefit Eligible"
              value={statusBreakdown.BENEFIT_ELIGIBLE || 0}
              subValue="Pending payouts"
              footer="Ready for admin approval"
            />
          </div>

          {/* Breakdown Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* By Referrer Type */}
            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <div className="flex items-center gap-2 mb-4">
                <Users size={18} className="text-purple-500" />
                <h3 className="font-bold text-[#111827]">By Referrer Type</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-purple-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <Users size={14} className="text-purple-600" />
                    </div>
                    <span className="text-sm font-semibold text-purple-700">Customer</span>
                  </div>
                  <p className="text-xl font-bold text-[#111827]">{bookings.by_customer?.count || 0} bookings</p>
                  <p className="text-sm text-slate-500">{formatAV(bookings.by_customer?.av_lacs || 0)}</p>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Award size={14} className="text-blue-600" />
                    </div>
                    <span className="text-sm font-semibold text-blue-700">Employee</span>
                  </div>
                  <p className="text-xl font-bold text-[#111827]">{bookings.by_employee?.count || 0} bookings</p>
                  <p className="text-sm text-slate-500">{formatAV(bookings.by_employee?.av_lacs || 0)}</p>
                </div>
              </div>
            </Card>

            {/* Status Breakdown */}
            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={18} className="text-emerald-500" />
                <h3 className="font-bold text-[#111827]">Referral Funnel</h3>
              </div>
              
              <div className="space-y-3">
                {[
                  { status: 'LOGGED', label: 'Logged', color: 'bg-slate-200' },
                  { status: 'VISIT_DONE', label: 'Visit Done', color: 'bg-amber-200' },
                  { status: 'BOOKING_CREATED', label: 'Booking Created', color: 'bg-cyan-200' },
                  { status: 'BOOKING_VERIFIED', label: 'Booking Verified', color: 'bg-indigo-200' },
                  { status: 'AGREEMENT_DONE', label: 'Agreement Done', color: 'bg-blue-200' },
                  { status: 'BENEFIT_ELIGIBLE', label: 'Benefit Eligible', color: 'bg-emerald-200' },
                ].map((item) => {
                  const count = statusBreakdown[item.status] || 0;
                  const total = Object.values(statusBreakdown).reduce((a, b) => a + b, 0) || 1;
                  const percentage = (count / total * 100).toFixed(0);
                  
                  return (
                    <div key={item.status} className="flex items-center gap-3">
                      <div className="w-28 text-xs font-medium text-slate-600">{item.label}</div>
                      <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${item.color} rounded-full transition-all duration-500`}
                          style={{ width: `${Math.max(percentage, 2)}%` }}
                        />
                      </div>
                      <div className="w-12 text-right text-xs font-bold text-slate-700">{count}</div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Quick Stats */}
          <Card className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 shadow-sm rounded-2xl">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <DollarSign size={24} className="text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Potential Payout Value</p>
                  <p className="text-xl font-bold text-[#111827]">
                    {formatAV((bookings.total_av_lacs || 0) * 0.005)}
                  </p>
                  <p className="text-xs text-slate-400">0.5% of {formatAV(bookings.total_av_lacs)} total AV</p>
                </div>
              </div>
              
              <div className="flex gap-6 text-center">
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{statusBreakdown.BENEFIT_ELIGIBLE || 0}</p>
                  <p className="text-xs text-slate-500">Pending Payouts</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{statusBreakdown.REJECTED || 0}</p>
                  <p className="text-xs text-slate-500">Rejected</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReferralDashboard;
